import { useLocal } from "@/pages/dashboard/utils/customHook";
import { downloadExcel, convertCommonTableToDownloadConfig } from "@/pages/dashboard/utils/download/index";
import { resolveDownloadColumns } from "@/pages/dashboard/utils/download/resolveDownloadColumns";
import TipContext from "@home/components/TipContext";
import React, { useMemo, useRef, useState } from "react";
import type { IDownloadConfig } from "@/pages/dashboard/utils/download/_type";
import { merge, get } from "lodash-es";
import http from '@/utils/http';
import { stringToFunction } from "@/pages/dashboard/utils/stringToJson";
import { IDownloadButtonProps, IBackendDownloadConfig, IApiDownloadConfig, IBeforeDownloadModalConfig, IDownloadModalField } from "./type";
import moment from "moment";
import { Modal, Form, Radio, Select, Button } from 'tea-component';
import { useNotificationOfDownloadTable } from '@/pages/dashboard/components/DownloadFormatModal';

const MAX_DOWNLOAD_ROWS = 50000;

/**
 * 将弹窗配置归一化为字段数组：
 * - 优先使用 fields（多字段，支持 radio / 下拉）
 * - 否则回退到 paramKey + options 的单字段简写（向后兼容，默认 radio）
 */
const normalizeModalFields = (modalConfig?: IBeforeDownloadModalConfig): IDownloadModalField[] => {
  if (!modalConfig) return [];
  if (Array.isArray(modalConfig.fields) && modalConfig.fields.length > 0) {
    return modalConfig.fields;
  }
  if (modalConfig.paramKey && Array.isArray(modalConfig.options)) {
    return [{
      paramKey: modalConfig.paramKey,
      type: 'radio',
      options: modalConfig.options,
    }];
  }
  return [];
};

export const DownloadButton = (props: IDownloadButtonProps) => {
  const { className, style, onClick, tableColumns, tableData, tableTitle, config, data, disabled: disabledProp, placement } = props;
  const { formatMessage, isEN } = useLocal();
  const [isDownloading, setIsDownloading] = useState(false);
  // 弹窗状态：modalValues 以 paramKey -> 选中值 的形式保存各字段选择
  const [modalVisible, setModalVisible] = useState(false);
  const [modalValues, setModalValues] = useState<Record<string, string>>({});
  const modalResolveRef = useRef<((value: Record<string, string> | null) => void) | null>(null);

  const downloadNoticeName = useMemo(() => {
    const apiConfig = config?.apiDownload;
    const backendConfig = config?.backendDownload;
    const nameFromConfig = isEN
      ? (apiConfig?.fileNameEn || backendConfig?.fileNameEn)
      : (apiConfig?.fileNameCn || backendConfig?.fileNameCn);
    return tableTitle || nameFromConfig || config?.fileName || '';
  }, [config, tableTitle, isEN]);

  const { setNoticeStatus } = useNotificationOfDownloadTable(downloadNoticeName);

  /**
   * 弹出选项弹窗，返回用户选择的各字段值（paramKey -> value，取消返回 null）
   */
  const showDownloadModal = (modalConfig: IBeforeDownloadModalConfig): Promise<Record<string, string> | null> => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;
      const fields = normalizeModalFields(modalConfig);
      const initValues: Record<string, string> = {};
      fields.forEach((field) => {
        initValues[field.paramKey] = field.defaultValue ?? field.options?.[0]?.value ?? '';
      });
      setModalValues(initValues);
      setModalVisible(true);
    });
  };
  
  const downloadConfig = useMemo<IDownloadConfig | null>(() => {
    const { fileName , tabConfig: propsTabConfig  } = config || {};

    const hasPropsColumns =
      Array.isArray(propsTabConfig?.columns) && propsTabConfig!.columns.length > 0;
    const hasPropsDataSource =
      Array.isArray(propsTabConfig?.dataSource) && propsTabConfig!.dataSource.length > 0;

    if (!hasPropsColumns && hasPropsDataSource) {
      const first = propsTabConfig!.dataSource[0];
      const embedded = first && typeof first === 'object' ? (first as any)._dlCols : undefined;
      if (Array.isArray(embedded) && embedded.length > 0) {
        return {
          fileName: fileName || tableTitle || 'file',
          tabs: [{
            tabName: propsTabConfig?.tabName || tableTitle || 'file',
            columns: embedded,
            dataSource: propsTabConfig!.dataSource,
            ...(propsTabConfig?.headerRows ? { headerRows: propsTabConfig.headerRows } : {}),
            ...(propsTabConfig?.mergeCells ? { mergeCells: propsTabConfig.mergeCells } : {}),
          }],
        };
      }
    }

    const tabConfig = convertCommonTableToDownloadConfig(
      tableData ?? [],
      tableColumns ?? [],
      tableTitle || 'file',
      isEN,
      { flattenGroupColumns: config?.flattenGroupColumns },
    );
    const finalTabConfig = merge({} ,tabConfig, propsTabConfig || {});

    return {
      fileName: fileName || tableTitle || 'file',
      tabs: [finalTabConfig],
    };
  }, [config, tableColumns, tableData, tableTitle, isEN]);

  /**
   * 解析 beforeRequest 参数（backendDownload 和 apiDownload 共用）
   */
  const resolveBeforeRequest = async (
    beforeRequest: string | ((d: Record<string, unknown>) => Record<string, unknown> | Promise<Record<string, unknown>> | false) | undefined,
    requestData: Record<string, unknown>,
  ): Promise<Record<string, unknown> | false> => {
    if (!beforeRequest) return {};
    if (typeof beforeRequest === 'string') {
      return stringToFunction(beforeRequest)(requestData);
    }
    return beforeRequest(requestData);
  };

  /**
   * 解析 API 下载的最终请求参数：
   * 合并 beforeRequest 结果与弹窗参数，过滤 clientOnly 字段
   */
  const resolveApiRequestParams = async (
    beforeRequest: IApiDownloadConfig['beforeRequest'],
    requestData: Record<string, unknown>,
    modalParamPatch: Record<string, unknown>,
    beforeDownloadModal: IBeforeDownloadModalConfig | undefined,
  ): Promise<Record<string, unknown> | false> => {
    const rawParams = await resolveBeforeRequest(beforeRequest, requestData);
    if (rawParams === false) return false;

    // 标记 clientOnly 的字段仅供前端使用，不下传后端（如 period_mode）
    const clientOnlyKeys = new Set(
      normalizeModalFields(beforeDownloadModal)
        .filter((field) => field.clientOnly)
        .map((field) => field.paramKey),
    );
    const requestParamPatch = Object.keys(modalParamPatch).reduce<Record<string, unknown>>((acc, key) => {
      if (!clientOnlyKeys.has(key)) {
        acc[key] = modalParamPatch[key];
      }
      return acc;
    }, {});
    return Object.assign({}, rawParams, requestParamPatch);
  };

  /**
   * 获取 API 下载的总行数（优先读缓存，其次发 probe 请求）
   */
  const fetchApiRowCount = async (
    api: string,
    method: string,
    finalParams: Record<string, unknown>,
    dataKey: string | undefined,
    rowCountKey: string,
    fixedPageSize: number | undefined,
  ): Promise<number> => {
    if (fixedPageSize && fixedPageSize > 0) {
      return fixedPageSize;
    }

    const existingData = dataKey ? get(data, `${dataKey}.data`) : undefined;
    if (existingData?.row_count !== undefined) {
      return Number(existingData.row_count);
    }
    if (existingData?.[rowCountKey] !== undefined) {
      return Number(existingData[rowCountKey]);
    }

    const probeResponse = (await http({
      url: api,
      method: method.toLowerCase(),
      [method === 'GET' ? 'params' : 'data']: { ...finalParams, page: 1, page_size: 1 },
      headers: { 'Content-Type': 'application/json' },
    })) as Record<string, unknown>;
    return Number(get(probeResponse, `data.${rowCountKey}`) || get(probeResponse, rowCountKey) || 0);
  };

  /**
   * 执行 API 全量数据拉取并写入 Excel
   */
  const executeApiDownload = async (
    api: string,
    method: string,
    finalParams: Record<string, unknown>,
    requestData: Record<string, unknown>,
    fileName: string,
    sheetName: string,
    rowCount: number,
    maxRows: number,
    formatData: IApiDownloadConfig['formatData'],
    customColumns: IApiDownloadConfig['columns'],
  ): Promise<void> => {
    const downloadPageSize = Math.min(rowCount, maxRows);
    const fullResponse = (await http({
      url: api,
      method: method.toLowerCase(),
      [method === 'GET' ? 'params' : 'data']: { ...finalParams, page: 1, page_size: downloadPageSize },
      headers: { 'Content-Type': 'application/json' },
    })) as Record<string, unknown>;

    let metricsList: Record<string, unknown>[] =
      (get(fullResponse, 'data.metrics_list') as Record<string, unknown>[]) ||
      (get(fullResponse, 'metrics_list') as Record<string, unknown>[]) ||
      [];

    if (formatData) {
      if (typeof formatData === 'string') {
        metricsList = stringToFunction(formatData)(metricsList, requestData) as Record<string, unknown>[];
      } else {
        metricsList = formatData(metricsList, requestData);
      }
    }

    if (!metricsList.length) {
      console.warn('[DownloadButton] apiDownload: no data to download');
      setNoticeStatus('error');
      return;
    }

    if (customColumns?.length) {
      const resolvedColumns = resolveDownloadColumns(customColumns, requestData);
      await downloadExcel({ fileName, tabs: [{ tabName: sheetName, dataSource: metricsList, columns: resolvedColumns }] }, isEN);
    } else {
      const tabConfig = convertCommonTableToDownloadConfig(
        metricsList,
        tableColumns ?? [],
        sheetName,
        isEN,
        { flattenGroupColumns: config?.flattenGroupColumns },
      );
      await downloadExcel({ fileName, tabs: [tabConfig] }, isEN);
    }
    setNoticeStatus('success');
  };

  /**
   * 宽表 API 全量下载处理函数
   */
  const handleApiDownload = async (apiConfig: IApiDownloadConfig): Promise<void> => {
    const {
      api,
      method = 'POST',
      fileNameEn,
      fileNameCn,
      sheetNameEn,
      sheetNameCn,
      beforeRequest,
      formatData,
      columns: customColumns,
      dataKey,
      rowCountKey = 'row_count',
      maxRows = MAX_DOWNLOAD_ROWS,
      fixedPageSize,
      beforeDownloadModal,
    } = apiConfig;

    const fileName = (isEN ? fileNameEn : fileNameCn) || tableTitle || 'Download';
    const sheetName = (isEN ? sheetNameEn : sheetNameCn) || fileName;

    try {
      // 如果配置了下载前弹窗，先弹出让用户选择（在 setIsDownloading 之前，避免组件灰置影响弹窗交互）
      let modalParamPatch: Record<string, unknown> = {};
      if (beforeDownloadModal) {
        const selectedValues = await showDownloadModal(beforeDownloadModal);
        if (selectedValues === null) {
          return;
        }
        modalParamPatch = selectedValues;
      }

      setIsDownloading(true);
      setNoticeStatus('loading');

      // 弹窗选择值需先合并进 requestData，beforeRequest / formatData 才能读到 period_mode 等字段
      // 同时透传 isEN，供 formatData 按语言选择中/英文名称
      const requestData: Record<string, unknown> = { ...data, ...modalParamPatch, isEN };
      const finalParams = await resolveApiRequestParams(beforeRequest, requestData, modalParamPatch, beforeDownloadModal);
      if (finalParams === false) {
        setIsDownloading(false);
        return;
      }

      const rowCount = await fetchApiRowCount(api, method, finalParams, dataKey, rowCountKey, fixedPageSize);
      if (!rowCount || rowCount <= 0) {
        console.warn('[DownloadButton] apiDownload: row_count is 0, skip download');
        setNoticeStatus('error');
        setIsDownloading(false);
        return;
      }

      await executeApiDownload(api, method, finalParams, requestData, fileName, sheetName, rowCount, maxRows, formatData, customColumns);
    } catch (error) {
      console.error('[DownloadButton] apiDownload failed:', error);
      setNoticeStatus('error');
    } finally {
      setIsDownloading(false);
    }
  };

  /** 清理 Excel sheet 名中的非法字符，并截断至 31 字符 */
  const sanitizeSheetName = (name?: string): string => {
    let base = (name || 'sheet1').trim();
    const forbidden = ['\\', '/', ':', '?', '*', '[', ']'];
    for (const ch of forbidden) {
      base = base.split(ch).join('');
    }
    const trimmed = base.length > 31 ? base.slice(0, 31) : base;
    return trimmed || 'sheet1';
  };

  /**
   * 将 Blob 响应写入本地文件（支持 JSZip / ExcelJS 重命名 sheet，降级为原始 Blob 下载）
   */
  const saveBlobResponse = async (
    // http 响应对象，结构依赖 axios-like 约定，使用 unknown 避免 any
    response: Record<string, unknown>,
    downloadFileName: string,
    sheetName: string,
  ): Promise<void> => {
    const contentType = (response?.headers as Record<string, unknown>)?.['content-type'] as string
      || (response?.headers as { get?: (k: string) => string; })?.get?.('content-type')
      || '';
    const isCsvDownload = /\.csv$/i.test(downloadFileName);
    // xlsx 才尝试改 sheet 名；CSV 为纯文本会直接走底部原始 Blob 下载
    const isXlsx = !isCsvDownload && (/spreadsheet|sheet|excel|office|zip/i.test(contentType) || /\.xlsx$/i.test(downloadFileName));

    const triggerDownload = (blob: Blob): void => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    if (sheetName && isXlsx) {
      const desired = sanitizeSheetName(sheetName);
      const responseData = response.data as Blob;

      try {
        const JSZip = (await import('jszip')).default;
        const arrayBuffer = await responseData.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        const workbookXmlPath = 'xl/workbook.xml';
        const file = zip.file(workbookXmlPath);
        if (file) {
          const escapeXml = (s: string): string =>
            s
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&apos;');
          const xml = await file.async('string');
          const replaced = xml.replace(/(<sheet\b[^>]*?name=")(.*?)("[^>]*>)/, (_, p1, _old, p3) => `${p1}${escapeXml(desired)}${p3}`);
          zip.file(workbookXmlPath, replaced);
          const outBlob = await zip.generateAsync({ type: 'blob' });
          triggerDownload(outBlob);
          setNoticeStatus('success');
          return;
        }
      } catch (e) {
        console.warn('[DownloadButton] JSZip rename sheet failed, fallback to ExcelJS:', e);
      }

      try {
        const exceljs = (await import('exceljs')).default;
        const arrayBuffer = await responseData.arrayBuffer();
        const workbook = new exceljs.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const firstSheet = workbook.worksheets?.[0];
        if (firstSheet) {
          firstSheet.name = desired;
        }
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        triggerDownload(blob);
        setNoticeStatus('success');
        return;
      } catch (e) {
        console.warn('[DownloadButton] ExcelJS 重命名 sheet 失败，回退为原始文件下载:', e);
      }
    }

    triggerDownload(new Blob([response.data as BlobPart]));
    setNoticeStatus('success');
  };

  /**
   * 后端下载处理函数
   */
  const handleBackendDownload = async (backendConfig: IBackendDownloadConfig): Promise<void> => {
    const {
      api,
      method = 'POST',
      params = {},
      fileNameEn,
      fileNameCn,
      sheetNameEn,
      sheetNameCn,
      headers = {},
      beforeRequest,
      resolveDownloadFileName,
    } = backendConfig;

    try {
      setIsDownloading(true);
      setNoticeStatus('loading');

      const requestData: Record<string, unknown> = { ...data };
      const legacyBase = (isEN ? fileNameEn : fileNameCn) || 'Download';
      const legacyDownloadFileName = `${legacyBase} ${moment().format('YYYY_MM_DD HH_mm_ss')}.xlsx`;

      let downloadFileName: string;
      if (resolveDownloadFileName) {
        const resolved = await resolveDownloadFileName(requestData);
        const trimmed = typeof resolved === 'string' ? resolved.trim() : '';
        if (trimmed.length > 0) {
          const lower = trimmed.toLowerCase();
          downloadFileName = (lower.endsWith('.xlsx') || lower.endsWith('.csv')) ? trimmed : `${trimmed}.xlsx`;
        } else {
          downloadFileName = legacyDownloadFileName;
        }
      } else {
        downloadFileName = legacyDownloadFileName;
      }

      let finalParams: Record<string, unknown> | false = params;
      if (beforeRequest) {
        if (typeof beforeRequest === 'string') {
          finalParams = stringToFunction(beforeRequest)(requestData);
        } else if (typeof beforeRequest === 'function') {
          finalParams = await beforeRequest(requestData);
        }
      }

      if (finalParams === false) {
        setIsDownloading(false);
        return;
      }

      const response = await http({
        url: api,
        method: method.toLowerCase(),
        [method === 'GET' ? 'params' : 'data']: finalParams,
        responseType: 'blob',
        download: true,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      }) as Record<string, unknown>;

      // 获取 sheet 名，默认与「旧版下载」文案基一致（非 resolveDownloadFileName 产物）
      const sheetName = (isEN ? sheetNameEn : sheetNameCn) || legacyBase;
      await saveBlobResponse(response, downloadFileName, sheetName);
    } catch (error) {
      console.error('后端下载失败:', error);
      setNoticeStatus('error');
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * 处理下载点击事件
   * 优先级：宽表API下载 > 后端下载 > onClick > 前端下载
   */
  const handleDownloadClick = async (): Promise<void> => {
    if (disabledProp || isDownloading) {
      return;
    }

    const apiDownloadConfig = config?.apiDownload;
    if (apiDownloadConfig?.api) {
      await handleApiDownload(apiDownloadConfig);
      return;
    }

    const backendDownloadConfig = config?.backendDownload;

    if (backendDownloadConfig?.api) {
      await handleBackendDownload(backendDownloadConfig);
      return;
    }

    if (onClick) {
      onClick();
      return;
    }

    if (downloadConfig) {
      try {
        await downloadExcel(downloadConfig);
      } catch (error) {
        console.error('前端下载失败:', error);
      }
    }
  };

  const isInactive = Boolean(disabledProp) || isDownloading;

  // 当前弹窗配置（从 apiDownload 里取）及归一化后的字段
  const modalConfig = config?.apiDownload?.beforeDownloadModal;
  const modalFields = useMemo(() => normalizeModalFields(modalConfig), [modalConfig]);

  const handleModalConfirm = () => {
    setModalVisible(false);
    modalResolveRef.current?.(modalValues);
    modalResolveRef.current = null;
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    modalResolveRef.current?.(null);
    modalResolveRef.current = null;
  };

  return (
    <>
      <div
        className={className}
        style={{
          ...style,
          ...(isInactive
            ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' as const }
            : undefined),
        }}
      >
        <TipContext
          tipText={formatMessage({ id: 'dbl_common_download' })}
          type={{ default: 'download', hover: 'download', }}
          placement={placement || 'top'}
          onClick={handleDownloadClick}
        />
      </div>
      {modalConfig && (
        <Modal
          visible={modalVisible}
          caption={
            (isEN ? modalConfig.titleEn : modalConfig.titleCn) ||
            formatMessage({ id: 'download_format_modal_title' })
          }
          onClose={handleModalCancel}
          size="s"
        >
          <Modal.Body>
            <Form>
              {modalFields.map((field) => {
                const fieldLabel = isEN ? field.labelEn : field.labelCn;
                const fieldValue = modalValues[field.paramKey] ?? '';
                const handleFieldChange = (value: string) =>
                  setModalValues((prev) => ({ ...prev, [field.paramKey]: value }));

                return (
                  <Form.Item key={field.paramKey} label={fieldLabel}>
                    {field.type === 'select' ? (
                      <Select
                        appearance="button"
                        size="full"
                        matchButtonWidth
                        value={fieldValue}
                        onChange={handleFieldChange}
                        placeholder={isEN ? field.placeholderEn : field.placeholderCn}
                        options={field.options.map((opt) => ({
                          value: opt.value,
                          text: isEN ? opt.labelEn : opt.labelCn,
                        }))}
                      />
                    ) : (
                      <Radio.Group value={fieldValue} onChange={handleFieldChange}>
                        {field.options.map((opt) => (
                          <Radio key={opt.value} name={opt.value}>
                            {isEN ? opt.labelEn : opt.labelCn}
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                  </Form.Item>
                );
              })}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button type="primary" onClick={handleModalConfirm}>
              {formatMessage({ id: 'download_format_modal_Confirm' })}
            </Button>
            <Button type="weak" onClick={handleModalCancel}>
              {formatMessage({ id: 'download_format_modal_Cancel' })}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};